/**
 * // src/types/Assets.d.ts
 *
 * This module defines an Asset interface and an AssetArray class. 
 * The AssetArray class encapsulates an array of Asset objects and provides 
 * several helper methods to retrieve various attributes of an Asset.
 */

export interface Asset {
    id: number;
    prettyname: string;
    chain: string;
    tinyName: string;
    shortName: string;
    longName: string;
    geckoName: string;
    binancePair: string;
}

export class AssetArray {
    private assets: Asset[];  // An array of assets

    /**
     * Constructor of the AssetArray class
     *
     * @param assets - an array of Asset objects
     */
    constructor(assets: Asset[]) {
        this.assets = assets;
    }

    /**
     * Static method to create an instance of AssetArray
     *
     * @param data - an array of Asset objects
     * @returns an instance of AssetArray
     */
    static fromData(data: Asset[]): AssetArray {
        return new AssetArray(data);
    }

    /**
     * The following functions return versions of the name of the asset, 
     * given another version of the name. We have:
     * 
     *  Short Name by (from) Long Name
     *  Tiny Name from Long Name
     *  Chain Name from Long Name
     *  Pretty Name from Short Name
     *  Pretty Name from Long Name
     *  CoinGecko Name from Long Name
     *  BinancePair  from Long Name
     */
    getShortNameByLongName(longName: string): string {
        const asset = this.assets.find((asset) => asset.longName === longName);
        return asset ? asset.shortName : '';
    }

    getTinyNameByLongName(longName: string): string {
        const asset = this.assets.find(asset => asset.longName === longName);
        return asset ? asset.tinyName : '';
    }

    getChainNameByLongName(longName: string): string {
        const asset = this.assets.find(asset => asset.longName === longName);
        return asset ? asset.chain : '';
    }

    getChainNameByShortName(shortName: string): string {
        const asset = this.assets.find(asset => asset.shortName === shortName);
        return asset ? asset.chain : '';
    }

    getPrettyNameByShortName(shortName: string): string {
        const asset = this.assets.find(asset => asset.shortName === shortName);
        return asset ? asset.prettyname : '';
    }

    getPrettyNameByLongName(longName: string): string {
        const asset = this.assets.find(asset => asset.longName === longName);
        return asset ? asset.prettyname : '';
    }

    getCoingeckoNameByLongName(longName: string): string {
        const asset = this.assets.find(asset => asset.longName === longName);
        return asset ? asset.geckoName : '';
    }

    getBinancePairByLongName(longName: string): string {
        const asset = this.assets.find(asset => asset.longName === longName);
        return asset ? asset.binancePair : '';
    }

    /**
     * Returns the number of Asset objects in the array.
     *
     * @returns the number of Asset objects
     */
    get length(): number {
        return this.assets.length;
    }

}
